/**
 * Work (company) email ownership verification.
 *
 * A working professional's company mail ID is the platform's core employment
 * signal, so it has to be proven rather than typed. This module drives a
 * first-party OTP challenge implemented by the `send-work-email-otp` and
 * `verify-work-email-otp` edge functions.
 *
 * Deliberately NOT built on `otpService.ts`: that module keeps codes in
 * sessionStorage (client-forgeable) and short-circuits company addresses with
 * "verified automatically without OTP". Neither is acceptable for a trust signal.
 *
 * Deliberately NOT built on `supabase.auth.signInWithOtp()`: auth OTPs operate on
 * the auth identity, so verifying one for a secondary address would provision a
 * second auth user or swap the current session.
 */

import { supabase } from '../lib/supabaseClient';

/**
 * Consumer mailbox and throwaway domains. Mirrors BLOCKED_EMAIL_DOMAINS in
 * `supabase/functions/send-work-email-otp/index.ts` — the server copy is
 * authoritative; this one exists purely to fail fast in the UI.
 */
const BLOCKED_EMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.co.in', 'yahoo.co.uk',
  'ymail.com', 'rocketmail.com', 'outlook.com', 'outlook.in', 'hotmail.com',
  'hotmail.co.uk', 'live.com', 'live.in', 'msn.com', 'aol.com', 'icloud.com',
  'me.com', 'mac.com', 'proton.me', 'protonmail.com', 'pm.me', 'gmx.com',
  'gmx.de', 'zoho.com', 'zohomail.com', 'yandex.com', 'yandex.ru', 'mail.com',
  'mail.ru', 'rediffmail.com', 'rediff.com', 'inbox.com', 'fastmail.com',
  'hey.com', 'tutanota.com', 'web.de', 'qq.com', '163.com', '126.com', 'naver.com',
  'mailinator.com', 'yopmail.com', 'guerrillamail.com', '10minutemail.com',
  'temp-mail.org', 'tempmail.com', 'throwawaymail.com', 'sharklasers.com',
  'getnada.com', 'dispostable.com', 'trashmail.com', 'maildrop.cc',
  'fakeinbox.com', 'mintemail.com', 'moakt.com', 'emailondeck.com',
  'spamgourmet.com', 'mytemp.email', 'tempr.email', 'discard.email',
  '1secmail.com', 'linshiyouxiang.net',
]);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export interface SendWorkEmailOtpResult {
  success: boolean;
  /** Server confirmed this address was already proven — skip straight to verified. */
  alreadyVerified?: boolean;
  email?: string;
  maskedEmail?: string;
  expiresAt?: string;
  cooldownSeconds?: number;
  maxAttempts?: number;
  sendsRemaining?: number;
  /** Only present when WORK_EMAIL_OTP_DEBUG_ECHO=true on the edge function. */
  devCode?: string;
  error?: string;
  /** Machine-readable reason: personal_domain | email_taken | cooldown | rate_limited | delivery_failed */
  code?: string;
  retryAfter?: number;
}

export interface VerifyWorkEmailOtpResult {
  success: boolean;
  alreadyVerified?: boolean;
  email?: string;
  verifiedAt?: string;
  error?: string;
  /** Machine-readable reason: invalid_code | attempts_exhausted | no_active_challenge */
  code?: string;
  attemptsRemaining?: number;
}

export interface WorkEmailProof {
  email: string;
  emailDomain: string;
  verifiedAt: string;
}

/** Normalises to the form the edge functions compare against. */
export function normalizeEmail(email: string): string {
  return (email || '').trim().toLowerCase();
}

/** True when the address looks syntactically usable. */
export function isValidEmailFormat(email: string): boolean {
  const value = normalizeEmail(email);
  return value.length > 0 && value.length <= 254 && EMAIL_RE.test(value);
}

/** True for consumer mailboxes and known disposable providers. */
export function isPersonalOrDisposableEmail(email: string): boolean {
  const domain = normalizeEmail(email).split('@')[1];
  return domain ? BLOCKED_EMAIL_DOMAINS.has(domain) : false;
}

/**
 * True when the address is worth sending a verification code to.
 * Syntactically valid and not a consumer/disposable mailbox.
 */
export function isVerifiableWorkEmail(email: string): boolean {
  return isValidEmailFormat(email) && !isPersonalOrDisposableEmail(email);
}

/**
 * Reads the caller's existing work-email proof, if any.
 * Backed by RLS (`auth.uid() = user_id`), so this can only ever return the
 * caller's own row. Returns null when nothing has been verified yet.
 */
export async function getVerifiedWorkEmail(): Promise<WorkEmailProof | null> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.user) return null;

    const { data, error } = await supabase
      .from('verified_work_emails')
      .select('email, email_domain, verified_at')
      .eq('user_id', session.user.id)
      .maybeSingle();

    if (error || !data) return null;
    return {
      email: String((data as any).email),
      emailDomain: String((data as any).email_domain),
      verifiedAt: String((data as any).verified_at),
    };
  } catch {
    // Missing table (migration not yet applied) or offline — treat as "not verified".
    return null;
  }
}

/** Normalises `functions.invoke` failures, which bury the JSON body in the error. */
async function readInvokeError(error: any, fallback: string): Promise<{ error: string; code?: string; retryAfter?: number; attemptsRemaining?: number }> {
  // supabase-js wraps non-2xx responses in a FunctionsHttpError whose `context`
  // is the raw Response. The useful message lives in that body, not in
  // error.message (which is just "Edge Function returned a non-2xx status code").
  try {
    const response = error?.context;
    if (response && typeof response.json === 'function') {
      const body = await response.json();
      if (body?.error) {
        return {
          error: String(body.error),
          code: body.code,
          retryAfter: body.retryAfter,
          attemptsRemaining: body.attemptsRemaining,
        };
      }
    }
  } catch {
    /* fall through to the generic message */
  }
  return { error: error?.message && !/non-2xx/i.test(error.message) ? error.message : fallback };
}

/** Requests a fresh 6-digit code for `email`. */
export async function sendWorkEmailOtp(email: string): Promise<SendWorkEmailOtpResult> {
  const cleanEmail = normalizeEmail(email);

  if (!isValidEmailFormat(cleanEmail)) {
    return { success: false, error: 'Enter a valid email address.' };
  }
  if (isPersonalOrDisposableEmail(cleanEmail)) {
    return {
      success: false,
      code: 'personal_domain',
      error: "Use your company email address — personal mailboxes can't be verified.",
    };
  }

  try {
    const { data, error } = await supabase.functions.invoke('send-work-email-otp', {
      body: { email: cleanEmail },
    });

    if (error) {
      const parsed = await readInvokeError(error, 'Could not send the code. Please try again.');
      return { success: false, ...parsed };
    }
    if (!data?.success) {
      return { success: false, error: data?.error || 'Could not send the code. Please try again.' };
    }
    return data as SendWorkEmailOtpResult;
  } catch (err: any) {
    return { success: false, error: err?.message || 'Could not send the code. Please try again.' };
  }
}

/** Redeems `code` against the live challenge for `email`. */
export async function verifyWorkEmailOtp(email: string, code: string): Promise<VerifyWorkEmailOtpResult> {
  const cleanEmail = normalizeEmail(email);
  const cleanCode = (code || '').replace(/\D/g, '');

  if (cleanCode.length !== 6) {
    return { success: false, error: 'Enter the 6-digit code from your email.' };
  }

  try {
    const { data, error } = await supabase.functions.invoke('verify-work-email-otp', {
      body: { email: cleanEmail, code: cleanCode },
    });

    if (error) {
      const parsed = await readInvokeError(error, 'Could not verify the code. Please try again.');
      return { success: false, ...parsed };
    }
    if (!data?.success) {
      return { success: false, error: data?.error || 'Could not verify the code. Please try again.' };
    }
    return data as VerifyWorkEmailOtpResult;
  } catch (err: any) {
    return { success: false, error: err?.message || 'Could not verify the code. Please try again.' };
  }
}
